import React, {useEffect, useState} from 'react';
import {Alert, ButtonGroup, Button, Form, Spinner, Table} from 'react-bootstrap';
import {getModbusTemplateIds} from '../../../services/modbusTemplateApi';
import {Dropdown} from '../../../../../shared/ui/Dropdown';

// ─── Parity options for RTU mode ─────────────────────────────────────────────
const PARITY_OPTIONS = ['none', 'even', 'odd', 'mark', 'space'];

// ─── Card (read-only summary shown on the device grid) ──────────────────────

const ModbusMeterTable = ({device}) => (
  <Table>
    <tbody>
      <tr>
        <th className="text-start">Typ</th>
        <td className="text-end">{device.deviceType}</td>
      </tr>
      <tr>
        <th className="text-start">Name</th>
        <td className="text-end">{device.name}</td>
      </tr>
      <tr>
        <th className="text-start">Template</th>
        <td className="text-end">{device.templateId || '—'}</td>
      </tr>
      <tr>
        <th className="text-start">Modbus ID</th>
        <td className="text-end">{device.modbusId}</td>
      </tr>
      <tr>
        <th className="text-start">{device.usbDevice ? 'USB Device' : 'IP Address'}</th>
        <td className="text-end">{device.usbDevice || device.ip || '—'}</td>
      </tr>
    </tbody>
  </Table>
);

// ─── Helpers to determine connection mode from existing device data ──────────

const inferMode = (device) => (device.usbDevice ? 'rtu' : 'tcp');

// ─── Modal form ──────────────────────────────────────────────────────────────

const ModbusMeterForm = ({device, setDevice, setSaveDisabled}) => {
  const [mode, setMode] = useState(inferMode(device));
  const [templateIds, setTemplateIds] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [templatesError, setTemplatesError] = useState(null);

  // Fetch available template IDs on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const result = await getModbusTemplateIds();
      if (cancelled) return;
      setTemplatesLoading(false);
      if (result.ok) {
        setTemplateIds(result.data);
      } else {
        setTemplatesError(result.error?.message || 'Unable to load templates.');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ── Validation ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const base = device.name && device.modbusId && device.templateId;
    const rtuOk = device.usbDevice && device.baudRate && device.parity;
    const tcpOk = device.ip && device.port;
    setSaveDisabled(!(base && (mode === 'rtu' ? rtuOk : tcpOk)));
  }, [device, mode, setSaveDisabled]);

  // ── Mode switch ─────────────────────────────────────────────────────────────
  const switchMode = (newMode) => {
    if (newMode === mode) return;
    setMode(newMode);

    if (newMode === 'rtu') {
      // Clear TCP-only fields, set RTU defaults
      setDevice((prev) => ({
        ...prev,
        ip: '',
        port: 502,
        usbDevice: prev.usbDevice || '/dev/ttyUSB0',
        baudRate: prev.baudRate || 9600,
        parity: prev.parity || 'even',
      }));
    } else {
      // Clear RTU-only fields, set TCP defaults
      setDevice((prev) => ({
        ...prev,
        usbDevice: '',
        baudRate: 9600,
        parity: 'even',
        ip: prev.ip || '',
        port: prev.port || 502,
      }));
    }
  };

  const handleChange = (e) => {
    const {name, value, type} = e.target;
    setDevice((prev) => ({...prev, [name]: type === 'number' ? Number(value) : value}));
  };

  return (
    <Form>
      {/* ── RTU / TCP toggle ─────────────────────────────────────────────────── */}
      <Form.Group className="mb-3">
        <Form.Label className="d-block">Connection Mode</Form.Label>
        <ButtonGroup className="w-100">
          <Button
            variant="outline-primary"
            active={mode === 'rtu'}
            onClick={() => switchMode('rtu')}
          >
            RTU
          </Button>
          <Button
            variant="outline-primary"
            active={mode === 'tcp'}
            onClick={() => switchMode('tcp')}
          >
            TCP
          </Button>
        </ButtonGroup>
      </Form.Group>

      {/* ── Common fields ────────────────────────────────────────────────────── */}
      <Form.Group className="mb-2">
        <Form.Label>Name</Form.Label>
        <Form.Control type="text" name="name" value={device.name} onChange={handleChange}/>
      </Form.Group>

      <Form.Group className="mb-2">
        <Form.Label>Template</Form.Label>
        {templatesLoading ? (
          <div className="d-flex align-items-center gap-2"><Spinner size="sm" animation="border"/> Loading templates…</div>
        ) : templatesError ? (
          <Alert variant="warning" className="py-1 mb-0">{templatesError}</Alert>
        ) : (
          <Dropdown
            name="templateId"
            options={templateIds.map((id) => ({value: id, label: id}))}
            value={device.templateId}
            onChange={handleChange}
            placeholder="Select template…"
            block
          />
        )}
      </Form.Group>

      <Form.Group className="mb-2">
        <Form.Label>Modbus ID</Form.Label>
        <Form.Control type="number" name="modbusId" value={device.modbusId} onChange={handleChange} min={1}/>
      </Form.Group>

      {/* ── RTU-specific fields ──────────────────────────────────────────────── */}
      {mode === 'rtu' && (
        <>
          <Form.Group className="mb-2">
            <Form.Label>USB Device</Form.Label>
            <Form.Control type="text" name="usbDevice" value={device.usbDevice} onChange={handleChange}/>
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Baud Rate</Form.Label>
            <Form.Control type="number" name="baudRate" value={device.baudRate} onChange={handleChange} min={1}/>
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Parity</Form.Label>
            <Dropdown
              name="parity"
              options={PARITY_OPTIONS.map((p) => ({value: p, label: p}))}
              value={device.parity}
              onChange={handleChange}
              block
            />
          </Form.Group>
        </>
      )}

      {/* ── TCP-specific fields ──────────────────────────────────────────────── */}
      {mode === 'tcp' && (
        <>
          <Form.Group className="mb-2">
            <Form.Label>IP Address</Form.Label>
            <Form.Control type="text" name="ip" value={device.ip} onChange={handleChange}/>
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Port</Form.Label>
            <Form.Control type="number" name="port" value={device.port} onChange={handleChange} min={1} max={65535}/>
          </Form.Group>
        </>
      )}
    </Form>
  );
};

/**
 * Strip mode-irrelevant fields before persisting.
 *  - TCP mode → remove usbDevice, baudRate, parity
 *  - RTU mode → remove ip, port
 */
const cleanModbusMeter = (device) => {
  const mode = inferMode(device);
  const clone = {...device};

  if (mode === 'tcp') {
    delete clone.usbDevice;
    delete clone.baudRate;
    delete clone.parity;
  } else {
    delete clone.ip;
    delete clone.port;
  }

  return clone;
};

export {ModbusMeterTable, ModbusMeterForm, cleanModbusMeter};




